import { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassPlus, faMagnifyingGlassMinus, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { format, parseISO, addDays, differenceInDays, isBefore, isAfter } from 'date-fns';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import toast from 'react-hot-toast';

export default function ZoomableTimeline({ experience, experienceId }) {
  const timeline = experience?.timeline || [];
  const experienceDate = experience?.date?.toDate ? experience.date.toDate() : 
                        (experience?.date ? new Date(experience.date) : new Date());
  
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const timelineRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper to parse dates (handles Firestore Timestamps, ISO strings, and Date objects)
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    let date = null;
    try {
      if (dateValue.toDate) {
        date = dateValue.toDate(); // Firestore Timestamp
      } else if (typeof dateValue === 'string') {
        date = parseISO(dateValue);
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      // Validate the date
      if (date && !isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Invalid date value:', dateValue, error);
    }
    return null;
  };

  // Calculate timeline bounds
  const timelineData = timeline.length > 0 ? timeline : [];
  
  // Get all dates from timeline
  const timelineDates = timelineData
    .map(m => m.date ? parseDate(m.date) : null)
    .filter(d => d !== null)
    .sort((a, b) => a - b);
  
  const startDate = timelineDates.length > 0 
    ? timelineDates[0]
    : addDays(experienceDate, -30);
  const endDate = timelineDates.length > 0
    ? timelineDates[timelineDates.length - 1]
    : addDays(experienceDate, 30);
  
  const totalDays = differenceInDays(endDate, startDate);
  const daysPerPixel = isMobile ? 0.5 : 1; // Base scale
  const timelineWidth = isMobile ? totalDays * daysPerPixel : totalDays * daysPerPixel * zoom;
  const timelineHeight = isMobile ? totalDays * daysPerPixel * zoom : 200;

  // Handle zoom
  const handleZoom = useCallback((delta) => {
    setZoom((prev) => {
      const newZoom = Math.max(0.5, Math.min(5, prev + delta));
      return newZoom;
    });
  }, []);

  // Handle wheel zoom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        handleZoom(delta);
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleZoom]);

  // Handle pan start
  const handlePanStart = (e) => {
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragStart({ 
      x: clientX - panX, 
      y: clientY - panY 
    });
  };

  // Handle pan move
  const handlePanMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    if (isMobile) {
      setPanY(clientY - dragStart.y);
    } else {
      setPanX(clientX - dragStart.x);
    }
  };

  // Handle pan end
  const handlePanEnd = () => {
    setIsDragging(false);
  };

  // Handle task toggle
  const handleTaskToggle = async (milestoneId, taskId, completed) => {
    if (!experienceId) return;
    try {
      const updatedTimeline = timeline.map((milestone) => {
        if (milestone.id === milestoneId) {
          return {
            ...milestone,
            tasks: milestone.tasks.map((task) =>
              task.id === taskId ? { ...task, completed: !completed } : task
            ),
          };
        }
        return milestone;
      });
      await updateDoc(doc(db, 'projects', experienceId), {
        timeline: updatedTimeline,
      });
      toast.success('Task updated');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  // Reset view
  const handleReset = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  // Calculate position for a milestone
  const getMilestonePosition = (milestone) => {
    if (!milestone.date) return { x: 0, y: 0 };
    const milestoneDate = parseDate(milestone.date);
    if (!milestoneDate) return { x: 0, y: 0 };
    const daysFromStart = differenceInDays(milestoneDate, startDate);
    
    if (isMobile) {
      return { y: daysFromStart * daysPerPixel * zoom, x: 0 };
    } else {
      return { x: daysFromStart * daysPerPixel * zoom, y: 0 };
    }
  };

  return (
    <div className="relative w-full h-[600px] md:h-[500px] bg-gray-50 rounded-2xl border-2 border-border overflow-hidden">
      {/* Controls */}
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        <button
          onClick={() => handleZoom(0.2)}
          className="bg-white rounded-lg p-2 shadow-lg border border-border hover:bg-gray-50 transition-colors"
          title="Zoom In"
        >
          <FontAwesomeIcon icon={faMagnifyingGlassPlus} className="w-4 h-4 text-text-primary" />
        </button>
        <button
          onClick={() => handleZoom(-0.2)}
          className="bg-white rounded-lg p-2 shadow-lg border border-border hover:bg-gray-50 transition-colors"
          title="Zoom Out"
        >
          <FontAwesomeIcon icon={faMagnifyingGlassMinus} className="w-4 h-4 text-text-primary" />
        </button>
        <button
          onClick={handleReset}
          className="bg-white rounded-lg p-2 shadow-lg border border-border hover:bg-gray-50 transition-colors"
          title="Reset View"
        >
          <FontAwesomeIcon icon={faArrowsRotate} className="w-4 h-4 text-text-primary" />
        </button>
      </div>

      {/* Zoom indicator */}
      <div className="absolute top-4 left-4 z-20 bg-white rounded-lg px-3 py-1.5 shadow-lg border border-border">
        <span className="text-sm font-semibold text-text-primary">
          {Math.round(zoom * 100)}%
        </span>
      </div>

      {/* Timeline container */}
      <div
        ref={containerRef}
        className="w-full h-full relative overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
        onTouchStart={handlePanStart}
        onTouchMove={handlePanMove}
        onTouchEnd={handlePanEnd}
      >
        {/* Timeline canvas */}
        <div
          ref={timelineRef}
          className="absolute"
          style={{
            transform: isMobile 
              ? `translateY(${panY}px) scale(${zoom})`
              : `translateX(${panX}px) scale(${zoom})`,
            transformOrigin: 'top left',
            width: isMobile ? '100%' : `${timelineWidth}px`,
            height: isMobile ? `${timelineHeight}px` : '100%',
            minWidth: isMobile ? '100%' : `${totalDays * daysPerPixel}px`,
            minHeight: isMobile ? `${totalDays * daysPerPixel}px` : '100%',
          }}
        >
          {/* Date grid lines (desktop horizontal) */}
          {!isMobile && (
            <div className="absolute inset-0">
              {Array.from({ length: Math.ceil(totalDays / 7) + 1 }).map((_, i) => {
                const date = addDays(startDate, i * 7);
                const daysFromStart = i * 7;
                const x = daysFromStart * daysPerPixel;
                return (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 border-l border-gray-200"
                    style={{ left: `${x}px` }}
                  >
                    <div className="absolute -top-6 left-0 text-xs text-text-secondary font-medium whitespace-nowrap">
                      {format(date, 'MMM d')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Date grid lines (mobile vertical) */}
          {isMobile && (
            <div className="absolute inset-0">
              {Array.from({ length: Math.ceil(totalDays / 7) + 1 }).map((_, i) => {
                const date = addDays(startDate, i * 7);
                const daysFromStart = i * 7;
                const y = daysFromStart * daysPerPixel;
                return (
                  <div
                    key={i}
                    className="absolute left-0 right-0 border-t border-gray-200"
                    style={{ top: `${y}px` }}
                  >
                    <div className="absolute -left-20 top-0 text-xs text-text-secondary font-medium whitespace-nowrap">
                      {format(date, 'MMM d')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Today indicator */}
          {(() => {
            const today = new Date();
            if (isBefore(today, startDate) || isAfter(today, endDate)) return null;
            const daysFromStart = differenceInDays(today, startDate);
            const position = daysFromStart * daysPerPixel;
            
            if (isMobile) {
              return (
                <div
                  className="absolute left-0 right-0 border-t-2 border-primary z-10"
                  style={{ top: `${position}px` }}
                >
                  <div className="absolute -left-24 top-0 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                    Today
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  className="absolute top-0 bottom-0 border-l-2 border-primary z-10"
                  style={{ left: `${position}px` }}
                >
                  <div className="absolute -top-6 left-0 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                    Today
                  </div>
                </div>
              );
            }
          })()}

          {/* Experience date indicator */}
          {(() => {
            const daysFromStart = differenceInDays(experienceDate, startDate);
            const position = daysFromStart * daysPerPixel;
            
            if (isMobile) {
              return (
                <div
                  className="absolute left-0 right-0 border-t-2 border-accent z-10"
                  style={{ top: `${position}px` }}
                >
                  <div className="absolute -left-24 top-0 bg-accent text-white text-xs font-bold px-2 py-1 rounded">
                    Event
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  className="absolute top-0 bottom-0 border-l-2 border-accent z-10"
                  style={{ left: `${position}px` }}
                >
                  <div className="absolute -top-6 left-0 bg-accent text-white text-xs font-bold px-2 py-1 rounded">
                    Event
                  </div>
                </div>
              );
            }
          })()}

          {/* Milestones */}
          {timelineData.map((milestone, index) => {
            const pos = getMilestonePosition(milestone);
            const milestoneDate = milestone.date ? parseDate(milestone.date) : null;
            
            return (
              <div
                key={milestone.id || index}
                className="absolute"
                style={isMobile 
                  ? { top: `${pos.y}px`, left: '20px', width: 'calc(100% - 40px)' }
                  : { left: `${pos.x}px`, top: '20px', width: '300px' }
                }
              >
                <div className="bg-white rounded-xl border-2 border-primary/50 shadow-lg p-4">
                  {/* Milestone header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        milestone.status === 'complete'
                          ? 'bg-primary-light'
                          : milestone.status === 'active'
                          ? 'bg-primary'
                          : 'bg-border'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-primary uppercase tracking-wide">
                        {milestoneDate ? format(milestoneDate, 'MMM d, yyyy') : (milestone.date || 'No date')}
                      </div>
                      {milestone.title && (
                        <div className="text-sm font-bold text-text-primary mt-1">
                          {milestone.title}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tasks */}
                  {milestone.tasks && milestone.tasks.length > 0 && (
                    <div className="space-y-2">
                      {milestone.tasks.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                            task.completed ? 'opacity-60' : ''
                          }`}
                          onClick={() => handleTaskToggle(milestone.id || index, task.id, task.completed)}
                        >
                          <div
                            className={`w-4 h-4 border rounded flex-shrink-0 mt-0.5 flex items-center justify-center ${
                              task.completed
                                ? 'bg-primary-light border-primary-light'
                                : 'border-border'
                            }`}
                          >
                            {task.completed && (
                              <span className="text-white text-xs font-semibold">✓</span>
                            )}
                          </div>
                          <div className="flex-1 text-xs text-text-primary font-medium">
                            {task.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
